import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { makeT } from "../lib/i18n";

const AppContext = createContext(null);

const CURRENCY_SYMBOLS = { SAR: "SAR", TRY: "₺", USD: "$" };
const CURRENCY_POSITION = { SAR: "after", TRY: "after", USD: "before" };

export const AppProvider = ({ children }) => {
    const [lang, setLang] = useState(() => localStorage.getItem("hs_lang") || "tr");
    const [currency, setCurrency] = useState(() => localStorage.getItem("hs_currency") || "SAR");
    const [theme, setTheme] = useState(() => localStorage.getItem("hs_theme") || "dark");
    const [admin, setAdmin] = useState(null);
    const [settings, setSettings] = useState({ deposit_percent: 10, whatsapp_number: "", iban: "", bank_name: "", account_holder: "" });
    const [rates, setRates] = useState({ base: "SAR", rates: { SAR: 1, TRY: 12.18, USD: 0.2667 }, source: "—", fetched_at: null });

    const t = useMemo(() => makeT(lang), [lang]);

    const switchLang = useCallback((l) => {
        setLang(l);
        localStorage.setItem("hs_lang", l);
    }, []);

    const switchCurrency = useCallback((c) => {
        setCurrency(c);
        localStorage.setItem("hs_currency", c);
    }, []);

    const switchTheme = useCallback((mode) => {
        setTheme((cur) => {
            const next = mode === "toggle" ? (cur === "dark" ? "light" : "dark") : mode;
            localStorage.setItem("hs_theme", next);
            return next;
        });
    }, []);

    // Apply theme class to <html>
    useEffect(() => {
        const root = document.documentElement;
        if (theme === "light") root.classList.add("light");
        else root.classList.remove("light");
    }, [theme]);

    const loadSettings = useCallback(async () => {
        try {
            const { data } = await api.get("/settings");
            if (data) setSettings((s) => ({ ...s, ...data }));
        } catch { /* settings load failed; defaults remain */ }
    }, []);

    const loadRates = useCallback(async () => {
        try {
            const { data } = await api.get("/rates");
            if (data?.rates) setRates(data);
        } catch { /* rates load failed; defaults remain */ }
    }, []);

    const checkAdmin = useCallback(async () => {
        const token = localStorage.getItem("hs_admin_token");
        if (!token) { setAdmin(null); return; }
        try {
            const { data } = await api.get("/auth/me");
            setAdmin(data);
        } catch {
            localStorage.removeItem("hs_admin_token");
            setAdmin(null);
        }
    }, []);

    useEffect(() => {
        loadSettings();
        loadRates();
        checkAdmin();
    }, [loadSettings, loadRates, checkAdmin]);

    // Refresh rates every 30 mins on the client side
    useEffect(() => {
        const id = setInterval(loadRates, 30 * 60 * 1000);
        return () => clearInterval(id);
    }, [loadRates]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("hs_admin_token", data.access_token);
        setAdmin(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("hs_admin_token");
        setAdmin(null);
    }, []);

    // Format price: input is in SAR (base), output is in selected currency
    const formatPrice = useCallback(
        (sarAmount, { showSymbol = true, decimals = null, currencyOverride = null } = {}) => {
            const cur = currencyOverride || currency;
            const rate = rates.rates?.[cur] ?? 1;
            const converted = (Number(sarAmount) || 0) * rate;
            const d = decimals !== null ? decimals : cur === "USD" ? 0 : 0;
            const formatted = converted.toLocaleString(lang === "tr" ? "tr-TR" : "en-US", {
                minimumFractionDigits: d,
                maximumFractionDigits: d,
            });
            if (!showSymbol) return formatted;
            const sym = CURRENCY_SYMBOLS[cur] || cur;
            return CURRENCY_POSITION[cur] === "before" ? `${sym}${formatted}` : `${formatted} ${sym}`;
        },
        [currency, rates, lang],
    );

    const value = useMemo(
        () => ({
            lang, switchLang, t,
            currency, switchCurrency, CURRENCY_SYMBOLS,
            theme, switchTheme,
            rates, loadRates, formatPrice,
            admin, login, logout, checkAdmin,
            settings, loadSettings,
        }),
        [lang, switchLang, t, currency, switchCurrency, theme, switchTheme, rates, admin, login, logout, settings, formatPrice, loadSettings, loadRates, checkAdmin],
    );

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);
